import { Link } from "@tanstack/react-router";
import { ArrowLeftIcon, FileCheckIcon } from "lucide-react";
import { forwardRef, useEffect, useRef, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { SubHeader } from "@/components/SubHeader";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { buttonVariants } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { ReportDropdown } from "@/features/accession/components/detail/ReportSection";
import { getApiUrl } from "@/lib/api";
import type { SubjectRouter, TReport, TSubjectDetail } from "@/lib/types";
import { format } from "@/lib/utils/date";
import { MultiColumnView } from "./SubjectMultiColumn";
import { useSubject } from "./SubjectProvider";

export function ReportView({ reportId }: { reportId: number | undefined }) {
  const { subject } = useSubject();
  const reports = trpc.subjects.reports.useQuery(subject.id);
  const selectedReport = reports.data?.find((r) => r.id === reportId);

  const isLoadingOrNotEmpty =
    reports.isLoading || (reports.data && reports.data.length > 0);

  return isLoadingOrNotEmpty ? (
    <MultiColumnView
      isDetailView={!!reportId}
      nav={
        <NavList
          items={reports.data || []}
          autoFocus
          className="divide-y"
          isLoading={reports.isPending}
          loadingItems={5}
          LoadingComponent={() => (
            <div className="flex flex-col gap-2 px-4 h-20 justify-center ">
              <div className="flex gap-2 w-full justify-between">
                <Skeleton className="w-20" />
                <Skeleton className="w-24" />
              </div>
              <div className="flex gap-2 w-full justify-between">
                <Skeleton className="w-20" />
                <Skeleton className="w-12" />
              </div>
            </div>
          )}
        >
          {(report) => (
            <NavListItem key={report.id} asChild>
              <ReportViewItem
                key={report.id}
                report={report}
                subject={subject}
              />
            </NavListItem>
          )}
        </NavList>
      }
    >
      {selectedReport ? (
        <>
          <SubHeader>
            <SubHeader.Left
              action={
                <Link
                  to="/subjects/$id/report"
                  params={{ id: subject.id }}
                  preload={false}
                  replace
                  className={buttonVariants({
                    variant: "ghost",
                    size: "icon",
                    className: "flex md:hidden",
                  })}
                >
                  <ArrowLeftIcon data-slot="icon" />
                </Link>
              }
            >
              <Breadcrumbs>
                <Breadcrumb
                  href={{
                    to: "/accessions/$id",
                    params: { id: selectedReport.accessionId },
                  }}
                >
                  {selectedReport.accessionId}
                </Breadcrumb>
                <Breadcrumb>{`report_${selectedReport.id}.pdf`}</Breadcrumb>
              </Breadcrumbs>
            </SubHeader.Left>
            <SubHeader.Right>
              <ReportDropdown
                report={{
                  ...selectedReport,
                  type: "report",
                  apiUrl: getApiUrl(
                    `/api/data/reports/${selectedReport.filePath}`,
                  ),
                }}
              />
            </SubHeader.Right>
          </SubHeader>
          <ReportIframe report={selectedReport} />
        </>
      ) : (
        <Centered>
          <Text muted>Select a report to view</Text>
        </Centered>
      )}
    </MultiColumnView>
  ) : (
    <Centered>
      <EmptyState>
        <EmptyStateIcon>
          <FileCheckIcon data-slot="icon" />
        </EmptyStateIcon>
        <EmptyStateHeading>No reports found</EmptyStateHeading>
        <EmptyStateDescription>
          No reports have been generated for this donor
        </EmptyStateDescription>
      </EmptyState>
    </Centered>
  );
}

function ReportIframe({ report }: { report: TReport }) {
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLIFrameElement>(null);
  const urlRef = useRef<string>(undefined);

  useEffect(() => {
    if (
      !loading &&
      ref.current &&
      report.filePath &&
      urlRef.current !== report.filePath
    ) {
      setLoading(true);
      urlRef.current = report.filePath;
      ref.current.contentWindow?.location.replace(
        getApiUrl(`/api/data/reports/${report.filePath}`),
      );
    }
  }, [loading, report.filePath]);

  return (
    <iframe
      ref={ref}
      title={report.id.toString()}
      height="100%"
      width="100%"
      onLoad={() => {
        setLoading(false);
      }}
    />
  );
}

const ReportViewItem = forwardRef<
  HTMLAnchorElement,
  {
    report: SubjectRouter["reports"][0];
    subject: TSubjectDetail;
  }
>(({ subject, report }, ref) => {
  const status = report.isCorrected
    ? "Corrected"
    : report.isPrelim
      ? "Prelim"
      : "Final";

  const color =
    status === "Corrected" ? "red" : status === "Final" ? "blue" : "gray";
  return (
    <Link
      ref={ref}
      className="flex flex-col gap-2 p-4 outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring focus-visible:rounded"
      to="/subjects/$id/report"
      params={{ id: subject.id }}
      activeProps={{ className: "bg-accent-muted" }}
      preload={false}
      replace
      search={(prev) => ({ ...prev, reportId: report.id })}
    >
      <div className="flex gap-2 w-full justify-between">
        <Badge>{report.id}</Badge>
        <Text size="1" muted mono>
          {format(report.createdAt)}
        </Text>
      </div>
      <div className="flex gap-2 w-full justify-between">
        <Text size="2">{report.accession.testType}</Text>
        <Badge color={color}>{status}</Badge>
      </div>
    </Link>
  );
});
